import { createContext, ReactNode, use, useMemo } from 'react';
import { Device, getDevice, isIPhone } from '../util/platform.ts';

const logAllEvents = false; // useful for isolating iOS Safari W3C audio element violations

export function useDualAudioElement(): DualAudioElementInterface {
  return use(DualAudioElementContext);
}

export interface DualAudioElementInterface {
  play(): Promise<void>;
  pause(): void;
  set playbackRate(rate: number);
  get playbackRate(): number;
  get paused(): boolean;
  get currentTime(): number;
  set currentTime(time: number);
  get duration(): number | undefined;
  setSrc(blob: Blob): Promise<void>;
  set loop(loop: boolean);
  addEventListener(eventName: string, callback: (event: Event) => void, { signal }: { signal: AbortSignal }): void;
}

const DualAudioElementContext = createContext<DualAudioElementInterface>({} as any);

export function DualAudioElementProvider({ children }: { children: ReactNode }) {
  const dualAudioElement = useMemo(() => new DualAudioElement(), []);
  return <DualAudioElementContext value={dualAudioElement}>{children}</DualAudioElementContext>;
}

export class DualAudioElement implements DualAudioElementInterface {
  private abortController = new AbortController();
  private readonly _elements: HTMLAudioElement[];
  private _current: HTMLAudioElement;
  private isSrcUpdatingResolver: (() => void) | undefined;
  private _pendingBlob: Blob | undefined;

  // We put the audio element in loop mode so that it plays the next verse automatically to avoid an Apple iOS bug
  // where it fails to go to the next verse.
  // When looping to the next verse, the audio element fails to fire the 'ended' event at the end of each verse.
  // Instead, it fires a 'seeked' event at the end of each verse.
  // However, it also fires the 'seeked' event whenever the time slider is moved.
  // We can avoid triggering the next verse on time slider events by checking that the current time is non-zero,
  // *unless* the customer restarts the song from the beginning, in which case we need to ignore the next 'seeked'.
  // Note that we translated the 'seeked' event into 'ended', so the software above does not have to change.
  private _userSeekPerformed = false;

  constructor({ loop }: { loop: boolean } = { loop: true }) {
    this._elements = [new Audio(), new Audio()];
    for (const [index, element] of this._elements.entries()) {
      element.loop = loop;
      element.id = `audio-element-${index + 1}`;
      element.playbackRate = 1;
      if (logAllEvents) {
        for (const event of audioEvents) {
          element.addEventListener(event, this.logAudioEvent.bind(this));
        }
      }
    }
    this._current = this._elements[0];
    const callback = this._onCanPlayThrough.bind(this);
    // iPhone audio elements do not properly fire 'canplaythrough' event (iPads do),
    // so we manufacture the event in the .src setter below
    if (!isIPhone()) {
      // _onCanPlayThrough will invoke external callbacks after swapping src <-> other
      this._elements[0].addEventListener('canplaythrough', callback, { signal: this.abortController.signal });
      this._elements[1].addEventListener('canplaythrough', callback, { signal: this.abortController.signal });
    }
  }

  logAudioEvent(e: any) {
    console.log(e.type, {
      index: this._elements.indexOf(e.target),
      currentTime: e.target.currentTime,
      paused: e.target.paused,
      readyState: e.target.readyState,
    });
  }

  destroy() {
    this.abortController.abort();
  }

  async play() {
    if (logAllEvents) {
      console.log(`DualAudioElement.play (index: ${this._elements.indexOf(this._current)})`);
    }
    await this._current.play();
  }

  pause() {
    if (logAllEvents) {
      console.log(`DualAudioElement.pause (index: ${this._elements.indexOf(this._current)})`);
    }
    void (async () => {
      this._current.pause();
    })();
  }

  set playbackRate(rate: number) {
    if (logAllEvents) {
      console.log(`DualAudioElement.set playbackRate (index: ${this._elements.indexOf(this._current)})`);
    }
    for (const element of this._elements) {
      element.playbackRate = rate;
    }
  }

  set loop(loop: boolean) {
    if (logAllEvents) {
      console.log(`DualAudioElement.set loop (index: ${this._elements.indexOf(this._current)})`);
    }
    for (const element of this._elements) {
      element.loop = loop;
    }
  }

  get playbackRate(): number {
    return this._current.playbackRate;
  }

  get paused(): boolean {
    return this._current.paused;
  }

  get currentTime() {
    return this._current.currentTime / this._current.playbackRate;
  }

  set currentTime(value: number) {
    if (logAllEvents) {
      console.log(`DualAudioElement.set currentTime (index: ${this._elements.indexOf(this._current)})`);
    }
    if (!Number.isFinite(value)) {
      console.error(`DualAudioElement.set currentTime: invalid value ${value}`);
      return;
    }
    this._userSeekPerformed = true;
    this._current.currentTime = value;
  }

  get duration(): number | undefined {
    const { duration, playbackRate } = this._current;
    if (isNaN(duration) || playbackRate === 0) {
      return;
    }
    return duration / playbackRate;
  }

  async setSrc(blob: Blob) {
    if (logAllEvents) {
      console.log(`DualAudioElement.set src (cur index: ${this._elements.indexOf(this._current)})`);
    }
    if (this.isSrcUpdatingResolver) {
      this._pendingBlob = blob;
      if (logAllEvents) {
        console.log(`DualAudioElement.set src already updating`);
      }
      return;
    }
    const promise = new Promise<void>((resolve) => {
      this.isSrcUpdatingResolver = () => resolve();
    });
    const prevSrc = this._other.src;
    this._other.src = (window.URL || window.webkitURL).createObjectURL(blob);
    this._other.playbackRate = 1;
    if (prevSrc) {
      (window.URL || window.webkitURL).revokeObjectURL(prevSrc);
    }
    if (isIPhone()) {
      // iPhone audio elements do not properly call canplaythrough (iPads do)
      this._other.load(); // necessary for iPhones after setting .src
      const other = this._other;
      setTimeout(() => {
        this._onCanPlayThrough({ target: other } as any as Event);
      }, 250);
    }
    await promise;
  }

  readonly _playthroughCallbacks: ((event: Event) => void)[] = [];

  addEventListener(eventName: string, callback: (event: Event) => void, { signal }: { signal: AbortSignal }) {
    if (eventName.toLowerCase() === 'canplaythrough') {
      // _onCanPlayThrough will invoke external callbacks after swapping src <-> other
      this._playthroughCallbacks.push(callback);
      return;
    }
    if (eventName.toLowerCase() === 'ended') {
      // when 'element.loop' is set to true, it will not fire the 'ended' event at the end of each verse
      // so, we fire that event manually
      this._elements[0].addEventListener('seeked', this._simulateEndedEvent(callback), { signal });
      this._elements[1].addEventListener('seeked', this._simulateEndedEvent(callback), { signal });
      // return;
    }
    this._elements[0].addEventListener(eventName, callback, { signal });
    this._elements[1].addEventListener(eventName, callback, { signal });
  }

  private _simulateEndedEvent(callback: (event: Event) => void): (event: Event) => void {
    return (event: Event) => {
      if (this._userSeekPerformed) {
        this._userSeekPerformed = false;
        return;
      }
      // During a src update (tempo/volume change), the audio element swap triggers seeked events
      // that are not real verse boundaries — suppress them to prevent accidental song transitions.
      // Note: this could miss a real verse boundary if tempo changes right at the end of a verse,
      // but that edge case is rare and handling it would require distinguishing swap-triggered seeks
      // from genuine verse-end seeks, which is not straightforward.
      if (this.isSrcUpdatingResolver) {
        return;
      }
      if (!this.paused) {
        callback(event);
      }
    };
  }

  private get _other() {
    return this._current === this._elements[0] ? this._elements[1] : this._elements[0];
  }

  private _onCanPlayThrough(event: Event) {
    if (!this.isSrcUpdatingResolver) {
      return;
    }
    if (event.target !== this._other) {
      // The current element can fire canplaythrough during normal browser buffering — ignore it
      return;
    }
    const current = this._current; // capture current b/c it might change
    const other = this._other; // capture current b/c it might change
    this._current = this._other;
    const synchronizeCurrentTime = () => {
      if (other.duration && current.duration) {
        const durationMultiplier = other.duration / current.duration;
        this._userSeekPerformed = true; // prevent verse advancement
        other.currentTime = current.currentTime * durationMultiplier + (current.paused ? 0 : this._playbackDelay());
      }
    };
    synchronizeCurrentTime();
    if (!current.paused) {
      // todo(hewitt): iOS fails to play the first time here (BUG_IOS_PLAY)
      void other.play();
      // allow the two MP3 files to overlap by 150ms to avoid an audible "gap" during the transition
      setTimeout(() => current.pause(), 150);
    }
    this._finishedUpdate();
    for (const callback of this._playthroughCallbacks) {
      callback(event);
    }
  }

  private _playbackDelay(): number {
    // During a volume or tempo change, we regenerate a new MP3 file while the old file is still playing,
    // then we start playing the new file at the exact playback location of the old file (synchronizeCurrentTime).
    // However, iOS devices are slow to adjust the playback location for the new MP3 file.
    // It seems to be a fairly consistent of 1/4 second, regardless of CPU speed (tested on old/new devices).
    // Thus, we set the playback location of the new MP3 file to be 1/4 second *ahead* of the old MP3 file
    // so that they mesh nicely, without a skip in the playback rhythm.
    // For other devices, only a very slight delay is needed.
    // We can further refine this hack based on user feedback.
    if (getDevice() === Device.iOS) {
      return 0.25;
    }
    return 0.075;
  }

  private _finishedUpdate() {
    this.isSrcUpdatingResolver?.();
    this.isSrcUpdatingResolver = undefined;
    if (!this._pendingBlob) {
      return;
    }
    const pendingBlob = this._pendingBlob;
    this._pendingBlob = undefined;
    void this.setSrc(pendingBlob);
  }
}

const audioEvents = [
  'loadstart',
  'progress',
  'suspend',
  'abort',
  'error',
  'emptied',
  'stalled',
  'loadedmetadata',
  'loadeddata',
  'canplay',
  'canplaythrough',
  'playing',
  'waiting',
  'seeking',
  'seeked',
  'ended',
  'durationchange',
  'timeupdate',
  'play',
  'pause',
  'ratechange',
  'resize',
  'volumechange',
];
